import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import {
  Bell,
  Shield,
  CalendarDays,
  ArrowLeftRight,
  CheckSquare,
  AlertTriangle,
  AlertOctagon,
  Info,
  Wallet,
  ExternalLink,
  Check,
  RefreshCw,
  X,
  Plus,
  BellPlus,
  Mail,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "../components/ui/sonner";
import { useConfirm } from "../components/ConfirmDialog";
import PageIntro from "../components/PageIntro";
import AddAlertDialog from "../components/AddAlertDialog";

const urgencyConfig = {
  critical: { icon: AlertOctagon, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20", label: "Critical" },
  high: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20", label: "High" },
  medium: { icon: Bell, color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20", label: "Medium" },
  low: { icon: CalendarDays, color: "text-white/65", bg: "bg-white/[0.03]", border: "border-white/[0.10]", label: "Low" },
};

const typeIcons = {
  visa_expiry: Shield,
  visa_expired: AlertOctagon,
  payment_milestone: CalendarDays,
  currency: ArrowLeftRight,
  custom_alert: BellPlus,
};

// Sensible renewal-term defaults per country.
const RENEW_PRESETS = {
  UAE: { years: 10, label: "+10y (Golden)" },
  "North Cyprus": { years: 2, label: "+2y" },
  Germany: { years: 3, label: "+3y" },
  default: { years: 2, label: "+2y" },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState(null); // `${notifId}:${stepIdx}`
  const [pendingAction, setPendingAction] = useState(null); // notif.id while in-flight
  const [renewOpenFor, setRenewOpenFor] = useState(null); // notif.id while renew form is open
  const [showAddAlert, setShowAddAlert] = useState(false);
  const confirm = useConfirm();

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markPaid = async (notif) => {
    if (!notif.asset_id || !notif.milestone_id) {
      toast.error("Missing milestone reference");
      return;
    }
    const ok = await confirm({
      title: "Mark milestone as paid?",
      body: `${notif.title}\n${notif.message}`,
      confirmText: "Mark paid",
      cancelText: "Cancel",
    });
    if (!ok) return;

    setPendingAction(notif.id);
    try {
      await api.put(`/assets/${notif.asset_id}/milestones/${notif.milestone_id}`, {
        is_paid: true,
        paid_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Milestone marked as paid");
      // Optimistic: drop locally before refetch finishes
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      fetchNotifications();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setPendingAction(null);
    }
  };

  const renewResidency = async (notif, newExpiry) => {
    if (!notif.residency_id) {
      toast.error("Missing residency reference");
      return;
    }
    setPendingAction(notif.id);
    try {
      await api.post(`/residencies/${notif.residency_id}/renew`, { expiry_date: newExpiry });
      toast.success(`Renewed — new expiry ${newExpiry}`);
      setRenewOpenFor(null);
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      fetchNotifications();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to renew");
    } finally {
      setPendingAction(null);
    }
  };

  // -- Custom alert actions --------------------------------------------------
  // Backed by /api/custom-alerts/{id} — complete soft-archives the alert,
  // delete hard-removes it. Both refresh the inbox.
  const completeCustomAlert = async (notif) => {
    const alertId = notif.custom_alert_id || notif.id;
    setPendingAction(notif.id);
    try {
      await api.post(`/custom-alerts/${alertId}/complete`);
      toast.success("Alert marked as done");
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      fetchNotifications();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to complete");
    } finally {
      setPendingAction(null);
    }
  };

  const deleteCustomAlert = async (notif) => {
    const alertId = notif.custom_alert_id || notif.id;
    const ok = await confirm({
      title: "Delete this alert?",
      body: `"${notif.title}" will be removed permanently. This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    setPendingAction(notif.id);
    try {
      await api.delete(`/custom-alerts/${alertId}`);
      toast.success("Alert deleted");
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      fetchNotifications();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setPendingAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="notifications-page">
      <PageIntro
        pageKey="alerts"
        title="Smart Alerts"
        subtitle="Visa renewals, payment milestones, compliance deadlines — and any reminder you add yourself — all in one inbox."
        bullets={[
          { icon: Shield, label: "Visa expiry watch", text: "Residencies expiring within 6 months are surfaced with a country-specific renewal checklist + estimated cost." },
          { icon: CalendarDays, label: "Payment milestones", text: "Unpaid SPA milestones due within 60 days appear here — colour-coded by urgency (overdue / 3d / 14d / 30d)." },
          { icon: BellPlus, label: "Custom reminders", text: "Click \u2018Add Alert\u2019 to schedule your own — insurance renewals, tax deadlines, anything. Opt-in for an email on the trigger day." },
          { icon: Check, label: "One-click resolve", text: "\u2018Mark as paid\u2019, \u2018I\u2019ve renewed this\u2019, or \u2018Done\u2019 clears alerts directly from here." },
        ]}
      />
      <div className="mb-6 md:mb-8 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">Alerts</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">Smart Notifications</h1>
          <p className="text-white/60 text-xs sm:text-sm mt-1">Visa expiry reminders, payment milestones, compliance &amp; custom reminders</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddAlert(true)}
          data-testid="add-alert-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#B5952F] text-black text-xs tracking-[0.15em] uppercase font-medium transition-colors"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add Alert
        </button>
      </div>

      <div className="space-y-4">
        {notifications.length > 0 ? notifications.map((notif, i) => {
          const urg = urgencyConfig[notif.urgency] || urgencyConfig.low;
          const TypeIcon = typeIcons[notif.type] || Bell;
          const UrgIcon = urg.icon;
          const isPayment = notif.type === "payment_milestone";
          const isVisaExpiry = notif.type === "visa_expiry" || notif.type === "visa_expired";
          const isCustom = notif.type === "custom_alert";
          const busy = pendingAction === notif.id;
          const renewOpen = renewOpenFor === notif.id;

          return (
            <motion.div
              key={notif.id || i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`bg-[#0A0A0A] border ${urg.border} p-4 md:p-5`}
              data-testid={`notification-${i}`}
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className={`w-9 h-9 md:w-10 md:h-10 ${urg.bg} flex items-center justify-center flex-shrink-0`}>
                  <TypeIcon className={`w-4 h-4 ${urg.color}`} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <UrgIcon className={`w-3.5 h-3.5 ${urg.color}`} />
                    <span className={`text-[10px] uppercase tracking-wider font-medium ${urg.color}`}>{urg.label}</span>
                    {notif.date && <span className="text-white/55 text-[10px]">{notif.date}</span>}
                  </div>
                  <h3 className="text-white text-sm font-medium">{notif.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm mt-1">{notif.message}</p>

                  {/* ── Action buttons ──────────────────────────────────── */}
                  {(isPayment || isVisaExpiry) && !renewOpen && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {isPayment && (
                        <button
                          type="button"
                          onClick={() => markPaid(notif)}
                          disabled={busy}
                          data-testid={`notif-${i}-mark-paid`}
                          className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 bg-emerald-500/[0.08] text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/15 hover:border-emerald-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                        >
                          <Check className="w-3 h-3" /> {busy ? "Saving…" : "Mark as paid"}
                        </button>
                      )}
                      {isVisaExpiry && (
                        <button
                          type="button"
                          onClick={() => setRenewOpenFor(notif.id)}
                          disabled={busy}
                          data-testid={`notif-${i}-renew`}
                          className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 bg-[#D4AF37]/[0.08] text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                        >
                          <RefreshCw className="w-3 h-3" /> I've renewed this
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Custom-alert actions: Done + Delete ─────────────── */}
                  {isCustom && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => completeCustomAlert(notif)}
                        disabled={busy}
                        data-testid={`notif-${i}-complete`}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 bg-emerald-500/[0.08] text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/15 hover:border-emerald-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                      >
                        <Check className="w-3 h-3" /> {busy ? "Saving…" : "Done"}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCustomAlert(notif)}
                        disabled={busy}
                        data-testid={`notif-${i}-delete`}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 text-white/60 border border-white/10 hover:text-red-300 hover:border-red-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                      {notif.email_reminder && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#D4AF37]/70 ml-auto">
                          <Mail className="w-3 h-3" /> email on trigger
                        </span>
                      )}
                    </div>
                  )}

                  {isVisaExpiry && renewOpen && (
                    <RenewForm
                      notif={notif}
                      busy={busy}
                      onCancel={() => setRenewOpenFor(null)}
                      onSubmit={(newExpiry) => renewResidency(notif, newExpiry)}
                      index={i}
                    />
                  )}

                  {/* ── Renewal checklist (unchanged) ───────────────────── */}
                  {notif.checklist && notif.checklist.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65">
                          {notif.checklist_country === "UAE"
                            ? "UAE Renewal Checklist"
                            : notif.checklist_country === "North Cyprus"
                              ? "North Cyprus Residency Renewal Roadmap"
                              : "Renewal Checklist"}
                        </p>
                        {notif.estimated_cost && (
                          <span
                            data-testid={`notif-cost-${i}`}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 px-2.5 py-1"
                          >
                            <Wallet className="w-3 h-3" /> {notif.estimated_cost}
                          </span>
                        )}
                      </div>

                      {notif.checklist_warning && (
                        <div
                          className="flex items-start gap-2.5 bg-red-500/[0.06] border border-red-500/25 px-3 py-2.5 mb-3"
                          data-testid={`notif-warning-${i}`}
                        >
                          <AlertOctagon className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="text-[11px] leading-relaxed">
                            <span className="text-red-400 font-semibold uppercase tracking-wider text-[10px]">Important</span>
                            <span className="text-red-100/80 ml-2">{notif.checklist_warning}</span>
                          </div>
                        </div>
                      )}

                      <ol className="space-y-1.5">
                        {notif.checklist.map((item, j) => {
                          if (typeof item === "string") {
                            return (
                              <li key={j} className="flex items-center gap-2">
                                <CheckSquare className="w-3.5 h-3.5 text-white/55 flex-shrink-0" />
                                <span className="text-white/65 text-xs">{item}</span>
                              </li>
                            );
                          }
                          const expandKey = `${notif.id}:${j}`;
                          const isExpanded = expandedStep === expandKey;
                          return (
                            <li
                              key={j}
                              className="border border-white/[0.08] hover:border-white/[0.12] transition-colors"
                              data-testid={`notif-${i}-step-${item.step}`}
                            >
                              <button
                                type="button"
                                onClick={() => setExpandedStep(isExpanded ? null : expandKey)}
                                className="w-full flex items-start gap-3 px-3 py-2.5 text-left"
                              >
                                <span className="font-mono text-[10px] tracking-wider text-[#D4AF37] mt-0.5 flex-shrink-0">
                                  {String(item.step ?? j + 1).padStart(2, "0")}
                                </span>
                                <span className="flex-1 text-white/80 text-xs font-medium leading-snug">
                                  {item.title}
                                </span>
                                <Info
                                  className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${
                                    isExpanded ? "text-[#D4AF37]" : "text-white/60"
                                  }`}
                                />
                              </button>
                              {isExpanded && (
                                <div className="px-3 pb-3 pl-10 -mt-1 space-y-2">
                                  {item.description && (
                                    <p className="text-white/80 text-xs leading-relaxed">{item.description}</p>
                                  )}
                                  {item.link && (
                                    <a
                                      href={item.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid={`notif-${i}-step-${item.step}-link`}
                                      className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-[#D4AF37] hover:text-[#E8C757] transition-colors"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      {item.link_label || "Open"}
                                    </a>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        }) : (
          <div className="bg-[#0A0A0A] border border-white/[0.10] p-12 text-center">
            <Bell className="w-12 h-12 text-white/10 mx-auto mb-3" strokeWidth={1} />
            <p className="text-white/65 text-sm mb-1">No notifications</p>
            <p className="text-white/55 text-xs">You're all caught up. Add residencies and payment milestones to receive smart alerts.</p>
          </div>
        )}
      </div>

      <AddAlertDialog
        open={showAddAlert}
        onClose={() => setShowAddAlert(false)}
        onCreated={() => fetchNotifications()}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline renewal form — appears under a visa-expiry notification
// when "I've renewed this" is clicked.
// ─────────────────────────────────────────────────────────────────────────────

function RenewForm({ notif, busy, onCancel, onSubmit, index }) {
  const preset = RENEW_PRESETS[notif.country] || RENEW_PRESETS.default;

  // Default suggestion: today + preset years, computed once.
  const defaultDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + preset.years);
    return d.toISOString().slice(0, 10);
  })();

  const [date, setDate] = useState(defaultDate);

  const applyPreset = (years) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    setDate(d.toISOString().slice(0, 10));
  };

  return (
    <div
      className="mt-3 bg-[#070707] border border-[#D4AF37]/20 p-4 space-y-3"
      data-testid={`notif-${index}-renew-form`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/80 font-sans mb-1">
            New expiry date
          </p>
          <p className="text-white/65 text-xs font-sans">
            Enter the date your renewed {notif.visa_type || "permit"} expires.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`notif-${index}-renew-cancel`}
          className="text-white/65 hover:text-white transition-colors"
          aria-label="Cancel renewal"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid={`notif-${index}-renew-date-input`}
          className="bg-[#050505] border border-white/[0.12] text-white px-3 py-2 text-xs font-mono focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
          min={new Date().toISOString().slice(0, 10)}
        />
        {[
          { y: 1, l: "+1y" },
          { y: preset.years, l: preset.label },
          { y: preset.years === 5 ? 10 : 5, l: preset.years === 5 ? "+10y" : "+5y" },
        ].map((p) => (
          <button
            key={p.l}
            type="button"
            onClick={() => applyPreset(p.y)}
            data-testid={`notif-${index}-renew-preset-${p.y}y`}
            className="text-[10px] tracking-wider uppercase px-2.5 py-1.5 text-white/65 hover:text-[#D4AF37] border border-white/[0.10] hover:border-[#D4AF37]/30 transition-colors"
          >
            {p.l}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSubmit(date)}
          disabled={busy || !date}
          data-testid={`notif-${index}-renew-submit`}
          className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-2 bg-[#D4AF37] text-black hover:bg-[#B5952F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-medium"
        >
          <Check className="w-3 h-3" />
          {busy ? "Saving…" : "Confirm renewal"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-[11px] tracking-wider uppercase px-3 py-2 text-white/60 hover:text-white transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
